/**
 * EnhancedFormModal - Core Universal Form Modal Template
 *
 * Full-featured modal component for all form use cases with:
 * - Semantic color tokens (100% RULE 15 compliant)
 * - Consistent spacing system
 * - Character counter (auto-enabled)
 * - Loading skeleton states
 * - Submit overlay with blur
 * - Unsaved changes warning
 * - Preview panel support (auto split layout)
 * - Keyboard shortcuts (Ctrl+Enter, Escape)
 * - Auto-focus first field
 * - Responsive sizing
 * - Animated transitions
 *
 * @module enhanced-form-modal
 */

'use client';

import * as React from 'react';
import { LuX, LuTriangleAlert } from 'react-icons/lu';

import { Dialog } from '../modal';
import { Box } from '../box';
import { Text } from '../text';
import { HStack, VStack } from '../stack';
import { IconButton } from '../icon-button';
import { Spinner } from '../spinner';
import { Grid, GridItem } from '../grid';
import { Separator } from '../separator';
import { useBreakpointValue } from '../../hooks/use-breakpoint-value';

// ============================================================================
// Constants
// ============================================================================

/**
 * Mobile full-screen modal body height.
 * 100dvh (dynamic viewport, handles iOS Safari address bar) minus
 * ~80px modal header + ~80px modal footer = 160px overhead.
 */
export const MOBILE_MODAL_BODY_HEIGHT = 'calc(100dvh - 160px)';

// ============================================================================
// Types
// ============================================================================

/**
 * Modal size configuration
 */
export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'cover' | 'full';

/**
 * Responsive size configuration
 */
export interface ResponsiveSize {
  base?: ModalSize;
  sm?: ModalSize;
  md?: ModalSize;
  lg?: ModalSize;
  xl?: ModalSize;
}

/**
 * Preview panel position
 */
export type PreviewPosition = 'right' | 'bottom' | 'none';

/**
 * Motion preset for modal animations
 */
export type MotionPreset =
  | 'scale'
  | 'slide-in-bottom'
  | 'slide-in-top'
  | 'slide-in-left'
  | 'slide-in-right'
  | 'none';

/**
 * EnhancedFormModal component props
 */
export interface EnhancedFormModalProps {
  // ===== Core Props =====
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal closes
   */
  onClose: () => void;

  /**
   * Modal title
   */
  title: string;

  /**
   * Optional subtitle/description
   */
  subtitle?: string;

  /**
   * Modal content (typically a form component)
   */
  children: React.ReactNode;

  // ===== Size & Layout =====
  /**
   * Modal size (responsive or fixed)
   * @default {{ base: 'full', md: 'xl' }}
   */
  size?: ModalSize | ResponsiveSize;

  /**
   * Maximum height for modal body
   * @default '70vh'
   */
  maxBodyHeight?: string;

  /**
   * Scroll behavior - inside modal or outside
   * @default 'inside'
   */
  scrollBehavior?: 'inside' | 'outside';

  /**
   * Motion preset for animation
   * @default 'slide-in-bottom'
   */
  motionPreset?: MotionPreset;

  // ===== Preview Panel =====
  /**
   * Preview/summary content (auto-enables split layout when provided)
   */
  previewContent?: React.ReactNode;

  /**
   * Preview panel position
   * @default 'right'
   */
  previewPosition?: PreviewPosition;

  /**
   * Preview panel width (only for 'right' position)
   * @default 400
   */
  previewWidth?: number;

  /**
   * Whether preview panel should be sticky on scroll
   * @default true
   */
  previewSticky?: boolean;

  // ===== Loading States =====
  /**
   * Whether modal is loading initial data
   * Shows skeleton when true
   */
  isLoading?: boolean;

  /**
   * Whether form is submitting
   * Shows overlay with spinner when true
   */
  isSubmitting?: boolean;

  /**
   * Loading timeout in milliseconds
   * Shows error message if loading exceeds this
   * @default undefined (no timeout)
   */
  loadingTimeout?: number;

  /**
   * Callback when loading times out
   */
  onLoadingTimeout?: () => void;

  // ===== Character Counter =====
  /**
   * Enable automatic character counter for inputs
   * @default true
   */
  showCharacterCount?: boolean;

  // ===== Dirty Check =====
  /**
   * Enable unsaved changes warning
   * @default true
   */
  enableDirtyCheck?: boolean;

  /**
   * Whether form has unsaved changes
   */
  isDirty?: boolean;

  /**
   * Custom warning message for dirty check
   */
  dirtyWarningMessage?: string;

  // ===== Keyboard Shortcuts =====
  /**
   * Enable keyboard shortcuts (Ctrl+Enter = submit, Escape = close)
   * @default true
   */
  enableKeyboardShortcuts?: boolean;

  /**
   * Callback for Ctrl+Enter keyboard shortcut
   */
  onCtrlEnter?: () => void;

  // ===== Auto Focus =====
  /**
   * Auto-focus first input field when modal opens
   * @default true
   */
  autoFocusFirstField?: boolean;

  // ===== Close Behavior =====
  /**
   * Whether to prevent closing on outside click
   * @default false
   */
  preventClose?: boolean;

  /**
   * Custom aria label for close button
   * @default 'Close'
   */
  closeAriaLabel?: string;

  // ===== Footer Actions =====
  /**
   * Custom footer content — footer renders automatically when provided
   */
  footerContent?: React.ReactNode;

  /**
   * @deprecated No longer needed — footer auto-shows when footerContent is provided.
   * Kept for backward compatibility; will be removed in a future version.
   */
  showFooter?: boolean;

  // ===== Progress (Multi-step) =====
  /**
   * Show progress indicator for multi-step forms
   */
  showProgress?: boolean;

  /**
   * Current step (1-indexed)
   */
  currentStep?: number;

  /**
   * Total steps
   */
  totalSteps?: number;

  // ===== Additional =====
  /**
   * Custom header content (rendered before title)
   */
  headerIcon?: React.ReactNode;

  /**
   * Custom class name for the modal content
   */
  className?: string;

  /**
   * Z-index for modal (useful for nested modals)
   * @default undefined (uses Chakra default)
   */
  zIndex?: number;
}

// ============================================================================
// Component
// ============================================================================

/**
 * EnhancedFormModal - Universal form modal template
 *
 * Full-featured modal component with all proposed enhancements.
 * Supports simple CRUD forms, complex multi-step wizards, and everything in between.
 *
 * @example Basic usage
 * ```tsx
 * <EnhancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Add Customer"
 *   subtitle="Create a new customer record"
 * >
 *   <CustomerForm />
 * </EnhancedFormModal>
 * ```
 *
 * @example With preview panel
 * ```tsx
 * <EnhancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Edit Order"
 *   previewContent={<OrderPreview data={formData} />}
 *   previewPosition="right"
 *   previewWidth={400}
 * >
 *   <OrderForm />
 * </EnhancedFormModal>
 * ```
 *
 * @example Multi-step with progress
 * ```tsx
 * <EnhancedFormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Vehicle Registration"
 *   showProgress
 *   currentStep={2}
 *   totalSteps={4}
 *   enableDirtyCheck
 *   isDirty={isDirty}
 * >
 *   <VehicleFormStep2 />
 * </EnhancedFormModal>
 * ```
 */
export function EnhancedFormModal({
  // Core
  isOpen,
  onClose,
  title,
  subtitle,
  children,

  // Size & Layout
  size = { base: 'full', md: 'xl' },
  maxBodyHeight = '70vh',
  scrollBehavior = 'inside',
  motionPreset = 'slide-in-bottom',

  // Preview Panel
  previewContent,
  previewPosition = 'right',
  previewWidth = 400,
  previewSticky = true,

  // Loading States
  isLoading = false,
  isSubmitting = false,
  loadingTimeout,
  onLoadingTimeout,

  // Character Counter (handled by ValidatedInput)
  showCharacterCount: _showCharacterCount = true,

  // Dirty Check
  enableDirtyCheck = true,
  isDirty = false,
  dirtyWarningMessage = 'You have unsaved changes. Are you sure you want to close?',

  // Keyboard Shortcuts
  enableKeyboardShortcuts = true,
  onCtrlEnter,

  // Auto Focus
  autoFocusFirstField = true,

  // Close Behavior
  preventClose = false,
  closeAriaLabel = 'Close',

  // Footer
  footerContent,
  showFooter: _showFooter,

  // Progress
  showProgress = false,
  currentStep,
  totalSteps,

  // Additional
  headerIcon,
  className: _className,
  zIndex: _zIndex,
}: EnhancedFormModalProps) {
  // ============================================================================
  // State
  // ============================================================================

  const [showLoadingTimeout, setShowLoadingTimeout] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // On mobile full-screen, expand body to use all available space
  const responsiveMaxBodyHeight =
    useBreakpointValue({
      base: MOBILE_MODAL_BODY_HEIGHT,
      md: maxBodyHeight,
    }) ?? maxBodyHeight;

  // ============================================================================
  // Effects
  // ============================================================================

  // Loading timeout handler
  React.useEffect(() => {
    if (!isLoading || !loadingTimeout) {
      setShowLoadingTimeout(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowLoadingTimeout(true);
      onLoadingTimeout?.();
    }, loadingTimeout);

    return () => clearTimeout(timer);
  }, [isLoading, loadingTimeout, onLoadingTimeout]);

  // Auto-focus first field
  React.useEffect(() => {
    if (!isOpen || !autoFocusFirstField || isLoading) return;

    const timer = setTimeout(() => {
      const firstInput = contentRef.current?.querySelector<HTMLInputElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
      );
      firstInput?.focus();
    }, 100); // Small delay to ensure modal is fully rendered

    return () => clearTimeout(timer);
  }, [isOpen, autoFocusFirstField, isLoading]);

  // Keyboard shortcuts
  React.useEffect(() => {
    if (!isOpen || !enableKeyboardShortcuts) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Enter = submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        onCtrlEnter?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, enableKeyboardShortcuts, onCtrlEnter]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleClose = React.useCallback(() => {
    if (preventClose) return;

    // Dirty check warning
    if (enableDirtyCheck && isDirty) {
      const confirmed = window.confirm(dirtyWarningMessage);
      if (!confirmed) return;
    }

    onClose();
  }, [preventClose, enableDirtyCheck, isDirty, dirtyWarningMessage, onClose]);

  // ============================================================================
  // Derived Values
  // ============================================================================

  const hasPreview = !!previewContent && previewPosition !== 'none';
  const showSubmitOverlay = isSubmitting && !isLoading;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }: { open: boolean }) => !open && handleClose()}
      size={size}
      placement="center"
      scrollBehavior={scrollBehavior}
      motionPreset={motionPreset}
      closeOnInteractOutside={!preventClose && !isDirty}
      closeOnEscape={!preventClose && !isDirty}
      preventScroll
      trapFocus
      lazyMount
      unmountOnExit
    >
      {/* Backdrop */}
      <Dialog.Backdrop />

      {/* Positioner - Required for proper modal centering */}
      <Dialog.Positioner {...({} as React.ComponentProps<typeof Dialog.Positioner>)}>
        {/* Content */}
        {/*
          B-290: the responsive size `{ base: 'full', md: 'xl' }` leaks the
          `full` variant's `min-height: 100dvh` into the `xl` desktop variant —
          `xl` overrides max-width but never resets min-height, so on desktop the
          dialog is forced to full viewport height while a short wizard step
          (e.g. the review step) leaves a large empty area below the content.
          Reset min-height to `auto` from `md` up so the desktop dialog hugs its
          content and stays vertically centered; the base (mobile) full-screen
          behaviour is preserved. The body already caps overflow at maxBodyHeight.
        */}
        <Dialog.Content minHeight={{ md: 'auto' }}>
          <Box background="bg.default" borderRadius={{ base: 'none', md: 'lg' }} boxShadow="lg">
            {/* Header */}
            <Dialog.Header paddingBottom={4}>
              <HStack justifyContent="space-between" alignItems="flex-start" gap={4}>
                <HStack gap={3} flex={1}>
                  {/* Header Icon */}
                  {headerIcon && (
                    <Box
                      padding={2}
                      backgroundColor="bg.subtle"
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {headerIcon}
                    </Box>
                  )}

                  {/* Title & Subtitle */}
                  <VStack gap={1} alignItems="flex-start" flex={1}>
                    <Text fontSize="xl" fontWeight="bold" color="fg.default">
                      {title}
                    </Text>
                    {subtitle && (
                      <Text fontSize="sm" color="fg.muted" fontWeight="normal">
                        {subtitle}
                      </Text>
                    )}
                  </VStack>
                </HStack>

                {/* Close Button */}
                {!preventClose && (
                  <IconButton
                    aria-label={closeAriaLabel}
                    icon={<LuX />}
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  />
                )}
              </HStack>

              {/* Progress Indicator */}
              {showProgress && currentStep && totalSteps && (
                <VStack gap={2} marginTop={4}>
                  <HStack gap={2} width="100%">
                    <Box flex={1} height="2px" backgroundColor="bg.subtle" borderRadius="full">
                      <Box
                        width={`${(currentStep / totalSteps) * 100}%`}
                        height="full"
                        backgroundColor="colorPalette.solid"
                        borderRadius="full"
                        transition="width 0.3s ease"
                      />
                    </Box>
                    <Text fontSize="xs" color="fg.muted" minWidth="fit-content">
                      {currentStep}/{totalSteps}
                    </Text>
                  </HStack>
                </VStack>
              )}
            </Dialog.Header>

            {/* Body */}
            <Dialog.Body
              ref={contentRef}
              maxHeight={responsiveMaxBodyHeight}
              overflowY="auto"
              paddingBottom={6}
              position="relative"
            >
              {/* Loading State */}
              {isLoading ? (
                <VStack gap={4} align="stretch">
                  <Box height="40px" backgroundColor="bg.subtle" borderRadius="md" />
                  <Box height="80px" backgroundColor="bg.subtle" borderRadius="md" />
                  <Box height="40px" backgroundColor="bg.subtle" borderRadius="md" />
                  <Box height="40px" backgroundColor="bg.subtle" borderRadius="md" />

                  {showLoadingTimeout && (
                    <Box
                      padding={4}
                      backgroundColor="orange.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="orange.300"
                    >
                      <HStack gap={2}>
                        <Box color="orange.600">
                          <LuTriangleAlert />
                        </Box>
                        <Text fontSize="sm" color="orange.800">
                          Loading is taking longer than expected...
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              ) : hasPreview && previewPosition === 'right' ? (
                /* Split Layout with Preview Panel */
                <Grid gridTemplateColumns={{ base: '1fr', lg: `1fr ${previewWidth}px` }} gap={6}>
                  {/* Main Content */}
                  <GridItem>{children}</GridItem>

                  {/* Preview Panel */}
                  <GridItem>
                    <Box
                      position={{ base: 'relative', lg: previewSticky ? 'sticky' : 'relative' }}
                      top={previewSticky ? 6 : 0}
                      height="fit-content"
                      padding={4}
                      backgroundColor="bg.subtle"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="border.default"
                    >
                      <VStack gap={4} align="stretch">
                        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                          Preview
                        </Text>
                        <Separator />
                        {previewContent}
                      </VStack>
                    </Box>
                  </GridItem>
                </Grid>
              ) : hasPreview && previewPosition === 'bottom' ? (
                /* Stacked Layout with Bottom Preview */
                <VStack gap={6} align="stretch">
                  {/* Main Content */}
                  {children}

                  {/* Separator */}
                  <Separator />

                  {/* Preview Panel */}
                  <Box
                    padding={4}
                    backgroundColor="bg.subtle"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="border.default"
                  >
                    <VStack gap={4} align="stretch">
                      <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                        Preview
                      </Text>
                      <Separator />
                      {previewContent}
                    </VStack>
                  </Box>
                </VStack>
              ) : (
                /* Standard Layout (no preview) */
                children
              )}

              {/* Submit Overlay */}
              {showSubmitOverlay && (
                <Box
                  position="absolute"
                  inset={0}
                  backgroundColor="blackAlpha.400"
                  backdropFilter="blur(4px)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="md"
                  zIndex={10}
                >
                  <VStack gap={3}>
                    <Spinner size="lg" color="blue" />
                    <Text fontSize="sm" color="white" fontWeight="medium">
                      Saving changes...
                    </Text>
                  </VStack>
                </Box>
              )}
            </Dialog.Body>

            {/* Footer — auto-renders when footerContent is provided */}
            {footerContent && <Dialog.Footer paddingTop={4}>{footerContent}</Dialog.Footer>}
          </Box>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

EnhancedFormModal.displayName = 'EnhancedFormModal';
